import {AfterContentChecked, AfterViewInit, ChangeDetectorRef, Component, OnInit, Renderer2} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {Observable} from 'rxjs';
import {environment} from 'src/environments/environment';
import {AuthService, AuthState} from './auth/auth.service/auth.service';
import {ISidebar, SidebarService} from 'src/app/containers/layout/sidebar/sidebar.service';
import {AppSettingsService} from './shared/services/app-settings/app-settings.service';
import {AUTH_ATTEMPT, LOGOUT_MARKER, PATH_AUTOLOGIN, REDIRECT, UNAUTHORIZED} from './shared/constants/value';
import {ApplicationRouteSegments} from './shared/enums/enums';
import {LocalStorageService} from './shared/services/storage/local-storage/local-storage.service';
import {ModalService} from './shared/services/modal/modal.service';
import {GoogleServicesClient, UserClient, UserLanguageDto} from './api/portal-api.generated';
import {useScript} from './shared/helpers/use-script/use-script';
import {GA_ENDPOINT} from './shared/constants/links';
import {buildGAScript} from './shared/helpers/google-analytics/google-analytics';
import { LangService } from './shared/services/lang/lang.service';
import {DestroyService} from './shared/services/destroy/destroy.service';
import {takeUntil} from 'rxjs/operators';
import {PlatformLocation } from '@angular/common';

declare const gtag: (...args: any[]) => void;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [
    DestroyService,
  ]
})
export class AppComponent implements OnInit, AfterViewInit, AfterContentChecked {
  title = 'eCredits';

  version: string = environment.version;

  isMultiColorActive = environment.isMultiColorActive;

  sidebar$: Observable<ISidebar>;

  showPage = false;

  notAuthorized: boolean;

  private urlBeforeRedirect: string;

  constructor(
    public sidebarService: SidebarService,
    private authService: AuthService,
    private router: Router,
    private renderer: Renderer2,
    private localStorageService: LocalStorageService,
    private cd: ChangeDetectorRef,
    public modalService: ModalService,
    private readonly route: ActivatedRoute,
    private googleServicesClient: GoogleServicesClient,
    private readonly userClient: UserClient,
    private readonly langService: LangService,
    private destroy$: DestroyService,
    private platformLocation: PlatformLocation) {
  }

  ngOnInit() {
    if(this.platformLocation.href.includes('resetsession=1')) {
      this.router.navigate([PATH_AUTOLOGIN], {queryParams: {resetsession: 0}});
    } else {
      this.checkAuth();
      this.sidebar$ = this.sidebarService.getSidebar();
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.renderer.addClass(document.body, 'show');
    }, 1000);
    setTimeout(() => {
      this.renderer.addClass(document.body, 'default-transition');
    }, 1500);
  }

  ngAfterContentChecked() {
    this.cd.detectChanges();
  }

  private checkAuth(): void {
    this.authService.checkAuth()
      .subscribe(async (authState) => {
        const {pathname, href, search} = window.location;

        switch (authState) {
          case AuthState.notAuthenticated: {
            this.localStorageService.remove(LOGOUT_MARKER);
            const routeName = this.extractRouteFromPathname(pathname);

            if (pathname !== PATH_AUTOLOGIN) {
              if (this.router.config.find(({path}) => path === routeName)?.data?.bypassAutoLogin) {
                this.showPage = true;
                this.notAuthorized = true;
                break;
              }

              let authAttempt: number | undefined = this.localStorageService.read(AUTH_ATTEMPT);
              this.localStorageService.write(AUTH_ATTEMPT, !authAttempt ? 1 : authAttempt++);

              if (!authAttempt) {
                this.localStorageService.write(REDIRECT, pathname + search);
              }

              if (this.router.config.find(({path}) => path === routeName)?.data?.bypassAutoLogin) {
                this.showPage = true;
                this.notAuthorized = true;
                break;
              }

              if (authAttempt > 3) {
                break;
              }

              this.router.navigate([PATH_AUTOLOGIN]);
            }

            break;
          }
          case AuthState.incompleteRegistration: {
            window.location.href = AppSettingsService.authServiceUrl + '/Account/Confirm?ReturnUrl=' + href;
            break;
          }
          default: {
            /*todo commented logOut marker*/
            /*if (this.localStorageService.read(LOGOUT_MARKER)) {
              this.authService.logoff();
              break;
            }*/

            await this.initGoogleServices();

            this.initLang();
            this.showPage = true;
            this.navigateToStoredEndpoint();
            break;
          }
        }
      });
  }

  private navigateToStoredEndpoint() {
    const path = this.localStorageService.read(REDIRECT);
    const currentUrl = this.router.url;


    if (currentUrl === path || !path) {
      this.localStorageService.remove(REDIRECT);
      this.localStorageService.remove(AUTH_ATTEMPT);
      return;
    }

    if (path.toString().includes(UNAUTHORIZED)) {
      this.router.navigate([ApplicationRouteSegments.unauthorized])
        .then(() => this.localStorageService.write(REDIRECT, '/'));
    } else if (!this.localStorageService.read(AUTH_ATTEMPT)) {
      this.localStorageService.remove(AUTH_ATTEMPT);
      return;
    } else {
      this.router.navigateByUrl(path).then(() => this.localStorageService.remove(REDIRECT));
    }

    this.localStorageService.remove(AUTH_ATTEMPT);
  }

  private extractRouteFromPathname(pathname: string): string {
    return pathname.split('/')[1];
  }

  private async initGoogleServices() {
    try {
      const {analyticsMeasurementId, siteKey} = await this.googleServicesClient.getConfigurations().toPromise();

      if(analyticsMeasurementId) {
        this.initGoogleAnalytics(analyticsMeasurementId);
      }

      if(siteKey) {
        AppSettingsService.siteKey = siteKey;
      }
    }
    catch (e) {
    }
  }

  private initGoogleAnalytics(id: string) {
    useScript('src', GA_ENDPOINT + id, 'head', true);

    useScript('text', buildGAScript(id), 'head');

    this.initRouterEvents();
  }

  private initRouterEvents() {
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.sendAnalyticsEvent();
      }
    });
  }

  private initLang(): void {
    this.userClient.getUserLang().subscribe((response: UserLanguageDto) => this.langService.setLanguage(response.language));
  }

  private sendAnalyticsEvent(event: string = 'page_view') {
    const href: string = this.platformLocation.href;

    gtag('event', event, {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      page_path: href,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      page_referrer: this.urlBeforeRedirect,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      page_location: href });

    this.urlBeforeRedirect = href;
  }
}
